import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { doc, setDoc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { auth, db } from "./firebase";
import { useLanguage } from "./LanguageContext";
import NotificationBell from "./NotificationBell";
import { sendNotification } from "./notifications";

function EmployeeDashboard({ userData, employeeData, onUpdate }) {
  const [activeTab, setActiveTab] = useState("assigned");
  const [myJobs, setMyJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [patientContactMap, setPatientContactMap] = useState({});
  const [unavailableDates, setUnavailableDates] = useState(employeeData.unavailableDates || []);
  const [newDate, setNewDate] = useState("");
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [agencyInfo, setAgencyInfo] = useState(null);
  const navigate = useNavigate();
  const { lang, toggleLang, t } = useLanguage();

  useEffect(() => {
    if (activeTab === "assigned" || activeTab === "ongoing" || activeTab === "completed") {
      loadJobs();
    }
    if (activeTab === "profile") {
      loadAgencyInfo();
    }
  }, [activeTab]);

  const loadAgencyInfo = async () => {
    try {
      const snap = await getDoc(doc(db, "providers", employeeData.agencyId));
      if (snap.exists()) setAgencyInfo(snap.data());
    } catch (err) {
      console.error(err);
    }
  };

  const loadJobs = async () => {
    setLoadingJobs(true);
    try {
      const user = auth.currentUser;
      const q = query(collection(db, "bookings"), where("employeeId", "==", user.uid));
      const snap = await getDocs(q);
      const results = [];
      snap.forEach((d) => results.push({ id: d.id, ...d.data() }));
      setMyJobs(results);
      loadPatientContacts(results);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingJobs(false);
    }
  };

  const loadPatientContacts = async (jobsList) => {
    const newMap = {};
    for (const j of jobsList) {
      if (j.status !== "confirmed" && j.status !== "completed") continue;
      if (!j.patientId) continue;
      const snap = await getDoc(doc(db, "patientProfiles", j.patientId));
      if (snap.exists()) {
        newMap[j.id] = { photoUrl: snap.data().photoUrl, mobile: snap.data().mobile };
      }
    }
    setPatientContactMap(newMap);
  };

  const markJobCompleted = async (bookingId, patientId, jobDate) => {
    try {
      await setDoc(doc(db, "bookings", bookingId), { status: "completed" }, { merge: true });
      if (patientId) {
        await sendNotification(
          patientId,
          "Ihr Termin am " + jobDate + " wurde als abgeschlossen markiert. Bitte bewerten Sie den Service.",
          "Your appointment on " + jobDate + " has been marked as completed. Please rate the service."
        );
      }
      loadJobs();
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const cancelJob = async (booking) => {
    if (!window.confirm(lang === "de" ? "Auftrag wirklich stornieren?" : "Really cancel this job?")) return;
    try {
      await setDoc(doc(db, "bookings", booking.id), { status: "cancelled" }, { merge: true });
      if (booking.patientId) {
        await sendNotification(
          booking.patientId,
          "Ihr Termin am " + booking.date + " wurde storniert.",
          "Your appointment on " + booking.date + " has been cancelled."
        );
      }
      if (employeeData.agencyId) {
        await sendNotification(
          employeeData.agencyId,
          userData.name + " hat den Termin am " + booking.date + " storniert.",
          userData.name + " cancelled the appointment on " + booking.date + "."
        );
      }
      loadJobs();
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const addUnavailableDate = async () => {
    if (!newDate) return;
    if (unavailableDates.includes(newDate)) return;
    const updated = [...unavailableDates, newDate].sort();
    setUnavailableDates(updated);
    setNewDate("");
    await saveAvailability(updated);
  };

  const removeUnavailableDate = async (dateToRemove) => {
    const updated = unavailableDates.filter((d) => d !== dateToRemove);
    setUnavailableDates(updated);
    await saveAvailability(updated);
  };

  const saveAvailability = async (updatedDates) => {
    setSavingAvailability(true);
    try {
      const user = auth.currentUser;
      await setDoc(doc(db, "employees", user.uid), { unavailableDates: updatedDates }, { merge: true });
      if (onUpdate) onUpdate();
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setSavingAvailability(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  const qualificationLabel = (q) => {
    const map = {
      nursing_student: lang === "de" ? "Pflege-/Medizinstudent" : "Nursing/Medical Student",
      retired_nurse: lang === "de" ? "Ehemalige Pflegekraft" : "Retired Nurse",
      alltagsbegleiter: lang === "de" ? "Zertifizierter Alltagsbegleiter" : "Certified Companion",
      other: lang === "de" ? "Sonstige" : "Other",
    };
    return map[q] || q;
  };

  const today = new Date().toISOString().split("T")[0];
  const assignedJobs = myJobs.filter((j) => j.status === "confirmed" && j.date >= today);
  const ongoingJobs = myJobs.filter((j) => j.status === "confirmed" && j.date === today);
  const completedJobs = myJobs.filter((j) => j.status === "completed");

  const renderPatientContact = (jobId) => {
    const contact = patientContactMap[jobId];
    if (!contact) return null;
    return (
      <div style={styles.patientContactBox}>
        {contact.photoUrl && <img src={contact.photoUrl} alt="Patient" style={styles.patientPhoto} />}
        <div>
          <div style={{ fontWeight: 700, fontSize: 12 }}>
            {lang === "de" ? "Patient zur Erkennung" : "Patient for recognition"}
          </div>
          {contact.mobile && <div style={{ fontSize: 12, color: "#555" }}>{contact.mobile}</div>}
        </div>
      </div>
    );
  };

  return (
    <div style={styles.pageBackground}>
      <div style={styles.phoneFrame}>
        <div style={styles.header}>
          <div>
            <div style={styles.headerTitle}>{t("welcome")}</div>
            <div style={styles.headerSubtitle}>{userData.name}</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <NotificationBell />
            <button onClick={toggleLang} style={styles.langToggle}>
              {lang === "de" ? "EN" : "DE"}
            </button>
            <button onClick={handleLogout} style={styles.logoutBtn}>{t("logout")}</button>
          </div>
        </div>

        <div style={styles.content}>
          {activeTab === "assigned" && (
            <div>
              <div style={styles.sectionLabel}>{lang === "de" ? "ZUGEWIESENE AUFTRAGE" : "ASSIGNED JOBS"}</div>
              {loadingJobs && <p style={{ textAlign: "center", color: "#999" }}>{t("loading")}</p>}
              {!loadingJobs && assignedJobs.length === 0 && (
                <div style={styles.emptyState}>
                  <p>{lang === "de" ? "Keine zugewiesenen Auftrage." : "No assigned jobs."}</p>
                </div>
              )}
              {assignedJobs.map((j) => (
                <div key={j.id} style={styles.jobCard}>
                  <div style={styles.jobTitle}>{j.hospital}</div>
                  <div style={styles.jobMeta}>{j.date} - {j.time}</div>
                  <div style={styles.jobMeta}>{j.patientName}</div>
                  {renderPatientContact(j.id)}
                  <button onClick={() => cancelJob(j)} style={styles.cancelButton}>
                    {lang === "de" ? "Stornieren" : "Cancel"}
                  </button>
                </div>
              ))}
            </div>
          )}

          {activeTab === "ongoing" && (
            <div>
              <div style={styles.sectionLabel}>{lang === "de" ? "LAUFENDE AUFTRAGE" : "ONGOING JOBS"}</div>
              {loadingJobs && <p style={{ textAlign: "center", color: "#999" }}>{t("loading")}</p>}
              {!loadingJobs && ongoingJobs.length === 0 && (
                <div style={styles.emptyState}>
                  <p>{lang === "de" ? "Kein laufender Auftrag heute." : "No ongoing job today."}</p>
                </div>
              )}
              {ongoingJobs.map((j) => (
                <div key={j.id} style={styles.jobCard}>
                  <div style={styles.jobTitle}>{j.hospital}</div>
                  <div style={styles.jobMeta}>{j.time}</div>
                  <div style={styles.jobMeta}>{j.patientName}</div>
                  {renderPatientContact(j.id)}
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button onClick={() => markJobCompleted(j.id, j.patientId, j.date)} style={styles.completeButton}>
                      {lang === "de" ? "Abschliessen" : "Complete"}
                    </button>
                    <button onClick={() => cancelJob(j)} style={styles.cancelButtonInline}>
                      {lang === "de" ? "Stornieren" : "Cancel"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "completed" && (
            <div>
              <div style={styles.sectionLabel}>{lang === "de" ? "ABGESCHLOSSENE AUFTRAGE" : "COMPLETED JOBS"}</div>
              {loadingJobs && <p style={{ textAlign: "center", color: "#999" }}>{t("loading")}</p>}
              {!loadingJobs && completedJobs.length === 0 && (
                <div style={styles.emptyState}>
                  <p>{lang === "de" ? "Noch keine abgeschlossenen Auftrage." : "No completed jobs yet."}</p>
                </div>
              )}
              {completedJobs.map((j) => (
                <div key={j.id} style={styles.jobCard}>
                  <div style={styles.jobTitle}>{j.hospital}</div>
                  <div style={styles.jobMeta}>{j.date}</div>
                  <div style={styles.jobMeta}>{j.patientName}</div>
                  {renderPatientContact(j.id)}
                </div>
              ))}
            </div>
          )}

          {activeTab === "profile" && (
            <div>
              <div style={styles.field}>
                <label style={styles.label}>{t("name")}</label>
                <input value={employeeData.name} disabled style={{ ...styles.input, background: "#f5f5f5" }} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>{t("email")}</label>
                <input value={employeeData.email} disabled style={{ ...styles.input, background: "#f5f5f5" }} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>{lang === "de" ? "Telefonnummer" : "Phone Number"}</label>
                <input value={employeeData.phone} disabled style={{ ...styles.input, background: "#f5f5f5" }} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>{lang === "de" ? "Qualifikation" : "Qualification"}</label>
                <input value={qualificationLabel(employeeData.qualification)} disabled style={{ ...styles.input, background: "#f5f5f5" }} />
              </div>
              <p style={styles.hint}>
                {lang === "de"
                  ? "Anderungen an Ihrem Profil nehmen Sie bitte uber Ihren Arbeitgeber vor."
                  : "Please contact your employer to update your profile."}
              </p>

              {agencyInfo && (
                <div style={{ marginTop: 20 }}>
                  <div style={styles.sectionLabel}>{lang === "de" ? "MEINE AGENTUR" : "MY AGENCY"}</div>
                  <div style={styles.jobCard}>
                    <div style={styles.jobTitle}>{agencyInfo.bio}</div>
                    <div style={styles.jobMeta}>{agencyInfo.city}, {agencyInfo.state}</div>
                    <div style={styles.jobMeta}>{agencyInfo.phone}</div>
                    {agencyInfo.website && <div style={styles.jobMeta}>{agencyInfo.website}</div>}
                  </div>
                </div>
              )}

              <div style={{ marginTop: 20 }}>
                <div style={styles.sectionLabel}>{lang === "de" ? "NICHT VERFUGBAR AN" : "UNAVAILABLE ON"}</div>
                <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                  <input
                    type="date"
                    value={newDate}
                    onChange={(e) => setNewDate(e.target.value)}
                    style={{ ...styles.input, flex: 1 }}
                  />
                  <button onClick={addUnavailableDate} disabled={savingAvailability} style={styles.addDateButton}>
                    {lang === "de" ? "Hinzufugen" : "Add"}
                  </button>
                </div>

                {unavailableDates.length === 0 && (
                  <div style={styles.emptyState}>
                    <p>{lang === "de" ? "Keine Termine markiert. Sie gelten als verfugbar." : "No dates marked. You are considered available."}</p>
                  </div>
                )}

                {unavailableDates.map((d) => (
                  <div key={d} style={styles.dateRow}>
                    <span>{d}</span>
                    <button onClick={() => removeUnavailableDate(d)} style={styles.removeDateButton}>
                      {lang === "de" ? "Entfernen" : "Remove"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={styles.bottomNav}>
          <button onClick={() => setActiveTab("assigned")} style={activeTab === "assigned" ? styles.navItemActive : styles.navItem}>
            <div style={styles.navIcon}>Z</div>
            <div style={styles.navLabel}>{lang === "de" ? "Zugewiesen" : "Assigned"}</div>
          </button>
          <button onClick={() => setActiveTab("ongoing")} style={activeTab === "ongoing" ? styles.navItemActive : styles.navItem}>
            <div style={styles.navIcon}>L</div>
            <div style={styles.navLabel}>{lang === "de" ? "Laufend" : "Ongoing"}</div>
          </button>
          <button onClick={() => setActiveTab("completed")} style={activeTab === "completed" ? styles.navItemActive : styles.navItem}>
            <div style={styles.navIcon}>C</div>
            <div style={styles.navLabel}>{t("completed")}</div>
          </button>
          <button onClick={() => setActiveTab("profile")} style={activeTab === "profile" ? styles.navItemActive : styles.navItem}>
            <div style={styles.navIcon}>P</div>
            <div style={styles.navLabel}>{t("profile")}</div>
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  pageBackground: {
    minHeight: "100vh",
    background: "#dce6f2",
    display: "flex",
    justifyContent: "center",
    fontFamily: "'Segoe UI', sans-serif",
  },
  phoneFrame: {
    width: "100%",
    maxWidth: 420,
    minHeight: "100vh",
    background: "#f5f8fc",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 0 40px rgba(0,0,0,0.12)",
  },
  header: {
    background: "linear-gradient(135deg, #1a5fb4, #2f7fd6)",
    color: "#fff",
    padding: "20px 22px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerTitle: { fontSize: 13, opacity: 0.85 },
  headerSubtitle: { fontSize: 19, fontWeight: 700, marginTop: 2 },
  langToggle: {
    background: "rgba(255,255,255,0.2)",
    color: "#fff",
    border: "none",
    padding: "8px 10px",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  logoutBtn: {
    background: "rgba(255,255,255,0.2)",
    color: "#fff",
    border: "none",
    padding: "8px 14px",
    borderRadius: 8,
    fontSize: 13,
    cursor: "pointer",
  },
  content: {
    flex: 1,
    padding: "18px",
    paddingBottom: 100,
    boxSizing: "border-box",
  },
  sectionLabel: { fontSize: 12, color: "#888", fontWeight: 700, letterSpacing: 0.5, marginBottom: 10 },
  field: { marginBottom: 14 },
  label: { display: "block", fontSize: 12, color: "#333", marginBottom: 6, fontWeight: 600 },
  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #ccc",
    fontSize: 13,
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  hint: { fontSize: 12, color: "#777", marginTop: 10 },
  emptyState: { textAlign: "center", color: "#999", padding: "50px 0" },
  jobCard: {
    background: "#fff",
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
  },
  jobTitle: { fontWeight: 700, fontSize: 14, color: "#222" },
  jobMeta: { fontSize: 12, color: "#888", marginTop: 2 },
  addDateButton: {
    padding: "10px 16px",
    background: "#1a5fb4",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  dateRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "#fff",
    padding: "10px 14px",
    borderRadius: 10,
    marginBottom: 8,
    fontSize: 13,
  },
  removeDateButton: {
    padding: "4px 10px",
    background: "#fde8e8",
    color: "#c62828",
    border: "none",
    borderRadius: 6,
    fontSize: 12,
    cursor: "pointer",
  },
  completeButton: {
    flex: 1,
    padding: "8px",
    background: "#2e7d32",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  cancelButton: {
    width: "100%",
    padding: "8px",
    background: "#fde8e8",
    color: "#c62828",
    border: "none",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 8,
  },
  cancelButtonInline: {
    flex: 1,
    padding: "8px",
    background: "#fde8e8",
    color: "#c62828",
    border: "none",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  bottomNav: {
    background: "#fff",
    borderTop: "1px solid #eee",
    display: "flex",
    justifyContent: "space-around",
    padding: "10px 0",
  },
  navItem: {
    background: "none",
    border: "none",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    color: "#999",
    cursor: "pointer",
    fontSize: 11,
  },
  navItemActive: {
    background: "none",
    border: "none",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    color: "#1a5fb4",
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 700,
  },
  navIcon: {
    fontSize: 14,
    fontWeight: 700,
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: "#eee",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  navLabel: { fontSize: 10 },
  patientContactBox: {
    background: "#f0f6ff",
    borderRadius: 8,
    padding: "8px 12px",
    marginTop: 8,
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  patientPhoto: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    objectFit: "cover",
    border: "2px solid #1a5fb4",
  },
};

export default EmployeeDashboard;

